import { useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { formatApiError } from '@mustauto/utils';

/** `YYYY-MM-DDTHH:mm` no fuso de quem abre — o formato do `datetime-local`. */
function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Sem prazo, o campo abre em "amanhã 18h" — o atalho mais usado na abertura. */
function defaultDeadline(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(18, 0, 0, 0);
  return d;
}

/** "qua., 28/10, 18:00" — no card e no histórico da OS. */
export function formatDeadline(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A previsão de entrega no card da OS, com a troca. Antes ela só aparecia
 * quando a OS já tinha prazo, e não mudava: o prazo era o da abertura para
 * sempre (pedido de cliente, out/2026). Mesmo jeito da vaga (`OSSlotField`).
 *
 * OS encerrada não muda o prazo (a API recusa): ali só aparece o que ficou,
 * e nada quando não houve prazo. Apagar o prazo não existe — nem aqui nem no
 * app, porque o sync não tem como dizer "apaguei".
 */
export function OSDeadlineField({
  osId,
  deadline,
  closed,
  onSave,
  isSaving,
}: {
  osId: string;
  deadline: string | null;
  closed: boolean;
  onSave: (deadline: string) => Promise<unknown>;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (closed && !deadline) return null;

  const start = () => {
    setValue(toLocalInput(deadline ? new Date(deadline) : defaultDeadline()));
    setEditing(true);
  };

  const picked = value ? new Date(value) : null;
  const valid = !!picked && !Number.isNaN(picked.getTime());

  const save = async () => {
    if (!valid || !picked) return;
    // O campo não tem segundos, e o prazo escolhido no app pode ter: abrir e
    // salvar sem mexer não é mudar o prazo (nem vira linha no histórico).
    if (deadline && Math.floor(new Date(deadline).getTime() / 60_000) === Math.floor(picked.getTime() / 60_000)) {
      setEditing(false);
      return;
    }
    try {
      await onSave(picked.toISOString());
      setEditing(false);
      toast({ type: 'success', title: 'Prazo salvo', description: `Entrega ${formatDeadline(picked.toISOString())}` });
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível mudar o prazo',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  if (!editing) {
    return (
      <div>
        <p className="text-xs text-muted-foreground">Previsão</p>
        {closed ? (
          <p className="font-medium">{deadline ? formatDeadline(deadline) : ''}</p>
        ) : (
          <button
            type="button"
            onClick={start}
            className="group flex items-center gap-1.5 text-left"
            aria-label={deadline ? `Previsão ${formatDeadline(deadline)}. Mudar o prazo` : 'Definir o prazo de entrega'}
          >
            <span className={deadline ? 'font-medium' : 'text-sm text-muted-foreground'}>
              {deadline ? formatDeadline(deadline) : 'Sem prazo · definir'}
            </span>
            <Pencil size={12} className="text-muted-foreground group-hover:text-foreground" aria-hidden />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="col-span-2 space-y-2 rounded-md border p-3">
      <label htmlFor={`deadline-${osId}`} className="text-xs font-medium text-muted-foreground">
        Previsão de entrega
      </label>
      <input
        id={`deadline-${osId}`}
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="block h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
      />
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={isSaving}
          className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={isSaving || !valid}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Salvar
        </button>
      </div>
    </div>
  );
}
